import { type FormEvent, useEffect, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input } from '../components/form';
import { Alert, Button, Spinner } from '../components/ui';
import styles from './screens.module.css';

interface Citacao {
  label: string;
  pagina: number | null;
}
interface Resposta {
  resposta: string;
  origem: string;
  citacoes: Citacao[];
}

const DISCLAIMER =
  'Resposta orientativa, baseada na convenção oficial — em caso de dúvida, vale o documento.';

/** `somenteLeitura`: condomínio suspenso (#127) — perguntar é escrita e a API recusaria. */
export function Perguntas({ somenteLeitura }: { somenteLeitura: boolean }) {
  const [statusLabel, setStatusLabel] = useState('Carregando…');
  const [pronto, setPronto] = useState(false);
  const [pergunta, setPergunta] = useState('');
  const [resposta, setResposta] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    void getJson<{ statusLabel: string; pronto: boolean }>('/api/convencao/status').then((d) => {
      setStatusLabel(d?.statusLabel ?? 'Não foi possível verificar a convenção.');
      setPronto(d?.pronto === true);
    });
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    const q = pergunta.trim();
    if (!q) return;
    setEnviando(true);
    const res = await mutateJson<Resposta>('/api/convencao/perguntar', { pergunta: q });
    setEnviando(false);
    if (res.status === 429) {
      setErro(res.data?.resposta ?? 'Limite diário de perguntas atingido. Tente novamente amanhã.');
      setResposta(null);
    } else if (res.ok && res.data) {
      setResposta(res.data);
    } else {
      setErro('Não consegui responder agora. Tente novamente.');
      setResposta(null);
    }
  }

  return (
    <div>
      <h1>Perguntas sobre a convenção</h1>
      <p className="muted">{statusLabel}</p>

      <form onSubmit={onSubmit} noValidate>
        <Field label="Sua pergunta">
          {(id) => (
            <Input
              id={id}
              type="text"
              maxLength={500}
              placeholder="Ex.: Posso ter animais no apartamento?"
              value={pergunta}
              disabled={!pronto || somenteLeitura}
              onChange={(e) => setPergunta(e.target.value)}
            />
          )}
        </Field>
        <Button type="submit" disabled={!pronto || somenteLeitura || enviando}>
          {enviando ? <Spinner /> : 'Perguntar'}
        </Button>
      </form>

      {erro ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Alert kind="warning">{erro}</Alert>
        </div>
      ) : null}

      {resposta ? (
        <div className={styles.qaAnswer}>
          <p className={styles.qaAnswerText}>{resposta.resposta}</p>
          {resposta.citacoes.length > 0 ? (
            <p className="muted" style={{ fontSize: 'var(--step--1)' }}>
              Trechos:{' '}
              {resposta.citacoes
                .map((c) => (c.pagina != null ? `${c.label} (p. ${c.pagina})` : c.label))
                .join(' · ')}
            </p>
          ) : null}
        </div>
      ) : null}

      <p className={styles.disclaimer}>{DISCLAIMER}</p>
    </div>
  );
}
